<template>
    <!-- https://vueform.com/reference/multiselect-element -->
    <div class="input-container text-start">

        <!-- O asterisco só é acrescentado quando o rótulo ainda não tem um. A
             convenção do repositório é escrevê-lo no próprio `label` (como o
             `InputField`, que não acrescenta nada), e somar os dois produzia
             "Tipo **" em todo select obrigatório do admin. -->
        <label v-if="label" class="form-label">{{ label }}<span v-if="required && !label.trim().endsWith('*')" class="form-label__obrigatorio">*</span></label>

        <div class="input-group mb-0 bg-light-subtle rounded-3">

            <span class="input-group-text text-muted" v-if="icon">
                <i :class="icon"></i>
            </span>

            <div class="flex-1">
                <MultiSelect
                    :required="required"
                    v-model="selected"
                    :options="options"
                    :label="labelOption"
                    :track-by="labelOption"
                    :valueProp="valueProp"
                    searchable
                    :mode="getMode()"
                    :placeholder="placeHolder"
                    noResultsText="Nenhuma opção encontrada."
                    noOptionsText="Nenhuma opção disponível."
                    :hideSelected="false"
                    :object="false"
                    :closeOnSelect="!multiple"
                    :closeOnDeselect="!multiple"
                    @update:modelValue="updateModelValue"
                    appendToBody
                    :canClear="canClear"
                    :disabled="disabled"
                    :classes="multiselectClasses"
                >
                    <template v-slot:multiplelabel="slotProps">
                        <!-- Guard `$slots` pelo mesmo motivo dos slots abaixo: sem ele,
                             todo select `multiple` já existente perderia seu rótulo. -->
                        <slot v-if="$slots.multiplelabel" name="multiplelabel" v-bind="slotProps" />
                        <div v-else class="multiselect-multiple-label">
                            {{ slotProps.values.map(item => item?.[labelOption] ?? item?.label ?? item?.value ?? item).join(', ') }}
                        </div>
                    </template>

                    <!-- Encaminha os slots de rótulo/opção do MultiSelect apenas quando o
                         pai os fornece. Sem o guard $slots, sobrescreveríamos a renderização
                         padrão de todos os usos existentes deste SelectField. -->
                    <template v-if="$slots.singlelabel" v-slot:singlelabel="slotProps">
                        <slot name="singlelabel" v-bind="slotProps" />
                    </template>
                    <template v-if="$slots.option" v-slot:option="slotProps">
                        <slot name="option" v-bind="slotProps" />
                    </template>
                </MultiSelect>
            </div>

        </div>

    </div>

</template>

<script>
export default {
  props: {
    label: {
      type: String
    },
    valueProp: {
      type: String
    },
    labelOption: {
      type: String
    },
    icon: {
      type: String
    },
    placeHolder: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    canClear: {
      type: Boolean,
      default: true
    },
    /** Repassado ao Multiselect: campo visível, porém travado (ex.: servidor Evolution na edição). */
    disabled: {
      type: Boolean,
      default: false
    },
    modelValue: {
      default: ''
    },
    options: {
      type: Array
    },
    /**
     * Classe extra aplicada ao PAINEL do dropdown.
     *
     * O painel é renderizado com `appendToBody`, ou seja, sai do componente e
     * vai parar direto no <body> — nenhum `<style scoped>` do pai o alcança.
     * Esta prop é a única via para um consumidor ajustar o painel (altura, por
     * exemplo, via `.ms-dropdown-alto` abaixo) sem mexer no default global,
     * que vale para as dezenas de selects do app.
     */
    dropdownClass: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      senhaVisivel: false,
      selected: this.modelValue
    }
  },
  computed: {
    // O `classes` do multiselect é mesclado sobre os defaults do pacote, então
    // basta reenviar a classe base do dropdown junto da extra.
    multiselectClasses () {
      return { dropdown: ['multiselect-dropdown', this.dropdownClass].filter(Boolean).join(' ') }
    },
    inputType () {
      if (this.type === 'password') {
        return this.senhaVisivel ? 'text' : 'password'
      }
      return this.type
    }
  },
  methods: {
    updateModelValue (newValue) {
      this.selected = newValue
      this.$emit('update:modelValue', newValue) // Atualiza o valor do props no componente pai
    },
    getMode () {
      return (this.multiple) ? 'multiple' : 'single'
    }
  },
  watch: {
    modelValue (newVal) {
      this.selected = newVal
    },
    selected (newVal) {
      this.$emit('update:modelValue', newVal) // Emite a alteração quando `selected` for alterado
    }
  }
}
</script>

<style>
.multiselect-dropdown {
    --ms-max-height: 15rem;
}

/*
 * Painel alto (o dobro do default), para selects cujas opções são cartões e
 * não uma linha de texto — hoje os de instância/agente de bot de IA no
 * ModalInbox. Aplicado via a prop `dropdownClass`; a especificidade de duas
 * classes vence o default acima.
 */
.multiselect-dropdown.ms-dropdown-alto {
    --ms-max-height: 30rem;
}
</style>
